import React from 'react';
import { useHotel } from './hotel/HotelTheme';
import Emblem from './hotel/Emblem';

// Lightweight, dependency-free icon set (stroke-based, inherits currentColor).
const base = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const make = (paths) => ({ size = 20, className = '', ...rest }) => (
  <svg {...base} style={{ width: size, height: size }} className={className} {...rest}>
    {paths}
  </svg>
);

// The mark. Under the Grand Hotel theme it is the hotel's emblem (an envelope
// under a pediment), so every screen that draws the logo, the public pages
// included, follows the switch without knowing about it.
export const Logo = ({ size = 28, className = '' }) => {
  const hotel = useHotel();
  if (hotel) return <Emblem size={size} />;
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className={className} aria-hidden>
      <defs>
        <linearGradient id="ms-logo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2563eb" />
          <stop offset="1" stopColor="#1d4ed8" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#ms-logo)" />
      <rect x="7" y="9" width="18" height="14" rx="2.5" fill="none" stroke="white" strokeWidth="2.2" />
      <path d="M7 11l9 6 9-6" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export const Sparkles = make(
  <>
    <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
    <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" />
  </>
);
export const Archive = make(
  <>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
    <path d="M10 12h4" />
  </>
);
export const Trash = make(
  <>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
    <path d="M10 11v6M14 11v6" />
  </>
);
export const Tag = make(
  <>
    <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V4a1 1 0 0 1 1-1h9l7.6 7.6a2 2 0 0 1 0 2.8z" />
    <circle cx="7.5" cy="7.5" r="1.5" />
  </>
);
export const Pin = make(
  <>
    <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6z" />
    <path d="M12 15v5" />
  </>
);
export const Inbox = make(
  <>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.5 5h13a2 2 0 0 1 1.8 1.1L22 12v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6l3.7-5.9A2 2 0 0 1 5.5 5z" />
  </>
);
export const Search = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </>
);
export const Refresh = make(
  <>
    <path d="M21 12a9 9 0 1 1-2.6-6.4" />
    <path d="M21 3v6h-6" />
  </>
);
export const Check = make(<path d="M20 6 9 17l-5-5" />);
export const X = make(<path d="M18 6 6 18M6 6l12 12" />);
export const Menu = make(<path d="M4 6h16M4 12h16M4 18h16" />);
export const ChevronRight = make(<path d="m9 6 6 6-6 6" />);
export const ChevronLeft = make(<path d="m15 18-6-6 6-6" />);
export const Shield = make(
  <>
    <path d="M12 3l8 3v6c0 5-3.4 8.3-8 9-4.6-.7-8-4-8-9V6l8-3z" />
    <path d="m9 12 2 2 4-4" />
  </>
);
export const Bolt = make(<path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13l0-8z" />);
export const Settings = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </>
);
export const LogOut = make(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </>
);
export const Users = make(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.9" />
    <path d="M16 3.1A4 4 0 0 1 16 11" />
  </>
);
export const Mail = make(
  <>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </>
);
export const Alert = make(
  <>
    <path d="M12 9v4M12 17h.01" />
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
  </>
);
export const Flame = make(
  <path d="M12 22c4 0 7-2.7 7-6.5 0-3.6-2.6-5.6-3.8-8.5-.5 1.6-1.4 2.6-2.4 3.2.2-2.2-.6-4.6-2.8-7.2-.3 3-2 4.4-3.4 6C5.3 10.5 5 12 5 13.5 5 17.3 8 22 12 22z" />
);
export const Keyboard = make(
  <>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M7 10h.01M11 10h.01M15 10h.01M17 10h.01M7 14h10" />
  </>
);
export const Undo = make(
  <>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h11a5 5 0 0 1 0 10h-1" />
  </>
);

export const Clock = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>
);

export const History = make(
  <>
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    <path d="M12 7v5l4 2" />
  </>
);

export const BellOff = make(
  <>
    <path d="M8.7 3.6A6 6 0 0 1 18 8c0 2 .4 3.6 1 5" />
    <path d="M6 8c0 4-2 5-2 5h12" />
    <path d="M10.3 21a2 2 0 0 0 3.4 0" />
    <path d="M2 2l20 20" />
  </>
);

export const Star = make(
  <path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9L12 3.5z" />
);

export const Sun = make(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </>
);

export const Moon = make(<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />);

export const Monitor = make(
  <>
    <rect x="2" y="4" width="20" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </>
);

export const ChevronDown = make(<path d="m6 9 6 6 6-6" />);

export const Filter = make(<path d="M3 5h18l-7 8v6l-4 2v-8L3 5z" />);

export const Paperclip = make(
  <path d="M21 12.5 12.5 21a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10.4 19.1a2 2 0 0 1-2.8-2.8l8-8" />
);

export const Reply = make(
  <>
    <path d="M9 17 4 12l5-5" />
    <path d="M4 12h11a5 5 0 0 1 5 5v2" />
  </>
);

export const Download = make(
  <>
    <path d="M12 3v12" />
    <path d="m7 12 5 5 5-5" />
    <path d="M4 20h16" />
  </>
);

export const Upload = make(
  <>
    <path d="M12 21V9" />
    <path d="m7 12 5-5 5 5" />
    <path d="M4 4h16" />
  </>
);

export const Copy = make(
  <>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </>
);

export const Google = ({ size = 18, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2C39.9 35.5 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z" />
  </svg>
);

// Added for the public surface: the privacy section (Lock), the self-hosting
// pitch (Server) and the footer's source link (Code).
export const Lock = make(
  <>
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </>
);

export const Server = make(
  <>
    <rect x="3" y="4" width="18" height="7" rx="2" />
    <rect x="3" y="13" width="18" height="7" rx="2" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </>
);

export const Code = make(
  <>
    <path d="m9 17-5-5 5-5" />
    <path d="m15 7 5 5-5 5" />
  </>
);
